import * as vp from 'vopay';
import React, { FunctionComponent, useState, useEffect } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import buildUpdateWebhooksRows from './buildUpdateWebhooksRows.helper';
import { UpdateWebhooksHandler } from './types';
import { usePartnerData } from '@/providers/PartnerData.provider';

const { Box, Button, Title, Table, TextField, Toggle, BackButton } = vp;

type DefaultValues = {
  accountonboarding: string;
};

type ToggleValues = {
  accountonboarding: boolean;
};

type UpdateWebhookProps = {
  validatedWebhookEvents: vp.WebhookEvent[];
  defaultValues: DefaultValues;
  toggleValues: ToggleValues;
  setDefaultValues: React.Dispatch<React.SetStateAction<DefaultValues>>;
  setToggleValues: React.Dispatch<React.SetStateAction<ToggleValues>>;
  onSubmit: () => void;
  setIsVisible: (e: boolean) => void;
  handlers: UpdateWebhooksHandler;
};
type FC = FunctionComponent<UpdateWebhookProps>;

const UpdateWebhook: FC = (props: UpdateWebhookProps) => {
  const {
    validatedWebhookEvents,
    defaultValues,
    toggleValues,
    setDefaultValues,
    setToggleValues,
    onSubmit,
    setIsVisible,
    handlers,
  } = props;
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const [buttonIsDisabled, setButtonIsDisabled] = useState(true);
  const [validatedWebhookEventsList, setValidatedWebhookEventsList] = useState<vp.WebhookEvent[]>([]);
  const updateWebhookRows = buildUpdateWebhooksRows();
  const toggleHandleChange = (id: string) => {
    setToggleValues((prevValues) => ({
      ...prevValues,
      [id]: !prevValues[id as keyof ToggleValues],
    }));
  };

  useEffect(() => {
    Object.entries(toggleValues).forEach(([key, value]) => {
      if (value === false) {
        defaultValues[key as keyof DefaultValues] = '';
      }
    });
  }, [toggleValues]);

  useEffect(() => {
    const missingValues = Object.entries(toggleValues).some(
      ([key, value]) => value && defaultValues[key as keyof DefaultValues] === '',
    );

    setButtonIsDisabled(missingValues);
  }, [toggleValues, defaultValues]);

  return (
    <Formik
      enableReinitialize
      initialValues={defaultValues}
      onSubmit={() => {
        onSubmit();
      }}
      validateOnChange
      validateOnBlur
      validationSchema={createdValidationSchema()}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => {
        useEffect(() => {
          setDefaultValues(values);
        }, [values]);

        useEffect(() => {
          setValidatedWebhookEventsList(validatedWebhookEvents);
        }, [validatedWebhookEvents]);

        useEffect(() => {
          const hasErrors = Object.keys(errors).length !== 0;
          const missingValues = Object.entries(toggleValues).some(
            ([key, value]) => value && defaultValues[key as keyof DefaultValues] === '',
          );

          setButtonIsDisabled(missingValues || hasErrors);
        }, [toggleValues, defaultValues, errors]);

        useEffect(() => {
          const changedKeys = getChangedWebhookEvent(values, defaultValues);
          const updatedWebhookEventsList = validatedWebhookEventsList.filter((event) => !changedKeys.includes(event));
          setValidatedWebhookEventsList(updatedWebhookEventsList);
        }, [values]);

        const updateWebhookTable: vp.TableProps = {
          name: 'update-webhook-fields',
          columns: {
            Field: { header: 'Event' },
            Toggle: { header: 'Enable Events', align: 'left' },
            Input: { header: 'URL' },
            Retrigger: { header: '' },
          },
          rows: updateWebhookRows.map(({ label, id, preset }) => ({
            Field: label,
            Toggle: (
              <Box flx="row" jc="fs" ps="sm">
                <Toggle
                  name={id}
                  onBlur={handleBlur}
                  onChange={() => toggleHandleChange(id)}
                  value={toggleValues[id as keyof ToggleValues]}
                />
              </Box>
            ),
            Input: (
              <TextField
                preset={preset}
                key={id}
                name={id}
                variant="standard"
                placeholder="Provide URL"
                onBlur={handleBlur}
                disabled={!toggleValues[id as keyof ToggleValues]}
                onChange={handleChange}
                value={defaultValues[id as keyof DefaultValues]}
                error={
                  errors[id as keyof DefaultValues] && touched[id as keyof DefaultValues]
                    ? errors[id as keyof DefaultValues]
                    : null
                }
              />
            ),
            Retrigger:
              validatedWebhookEventsList.includes(id) &&
              defaultValues.accountonboarding &&
              defaultValues.accountonboarding !== '' ? (
                <Button
                  id="Webhook-review"
                  type="button"
                  color="secondary"
                  label="Retrigger"
                  onClick={() => {
                    handlers.setWebhookType({ label, id });
                    setIsVisible(true);
                  }}
                />
              ) : (
                ''
              ),
          })),
        };
        return (
          <form onSubmit={handleSubmit}>
            <Box mb="md" jc="fs" ai="center" gap="common">
              <BackButton variant="arrow" backButtonEnabled />
              <Title title="Manage Webhook" />
            </Box>
            <Table {...updateWebhookTable} />
            <Box mt="md" jc="fe">
              <Button
                id="Webhook-review"
                type="submit"
                label="Review"
                isDisabled={buttonIsDisabled || isRoleViewOnly}
              />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema() {
    return Yup.object().shape({
      accountonboarding: Yup.string().matches(
        /^(https?:\/\/)[-a-z0-9+&@#\/%?=~_|!:,.;]*[-a-z0-9+&@#/%=~_|]/i,
        'Please enter a valid URL.',
      ),
    });
  }

  function getChangedWebhookEvent(values: DefaultValues, defaultValuesChanged: DefaultValues): vp.WebhookEvent[] {
    const changedKeys = (Object.keys(values) as (keyof DefaultValues)[]).filter(
      (key) => values[key] !== defaultValuesChanged[key],
    );

    return changedKeys;
  }
};

export default UpdateWebhook;
